import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
  registerAthleteWithEventId,
  PostCandidateAthlete,
} from "../../../services/AthleteService";
import { useAxiosInstance } from "../../../services/Interceptor";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Nav from "../../../components/Nav";
import { useParams, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { GetEventsByEventId } from "../../../services/EventsService";
import {
  CheckValidCashToken,
  PostPayment,
} from "../../../services/PaymentService";
import { GetEventCombinePrice } from "../../../services/EventsService";
import { useDispatch } from "react-redux";
import {
  registerAccountStore,
  clearRegisterData,
} from "../../../store/slices/accountSlice";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../../components/LoadingSpinner";
import {
  positions,
  sports,
  states,
  tshirtSizes,
} from "../../../utils/PositionStateData";
import { BackButton } from "../../../components/backButton";
import { CheckIsEmailExist } from "../../../services/AccountService";
import Multiselect from "multiselect-react-dropdown";
import { divisions } from "../../../utils/PositionStateData";
import HighSchoolAutocomplete from "./HighSchoolAutocomplete";
import { useTrackEvent } from "../../../hooks/useTrackEvent";

const RegisterAthlete = () => {
  const { eventId } = useParams();
  const location = useLocation();
  const apiInstance = useAxiosInstance();
  const [eventNameSelected, setEventNameSelected] = useState();
  const [isRegistered, setIsRegistered] = useState(false);
  const [cashTokenNew, setCashTokenNew] = useState("");
  const [combinePrice, setCombinePrice] = useState(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const primaryPositions = positions.filter((p) => p !== "None");
  const trackEvent = useTrackEvent();

  useEffect(() => {
    trackEvent("Registration0_EventsPage", {
      event_id: eventId,
      portal_type: "highschool",
    });
  }, [eventId, trackEvent]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const data = await GetEventsByEventId(apiInstance, eventId);
        if (data) {
          setEventNameSelected(`${data.date} - ${data.name} - ${data.city}`);
          trackEvent("Registration1_OpenForm", {
            event_id: eventId,
            event_name: data.name,
            event_date: data.date,
            portal_type: "highschool",
          });
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadEvents();
  }, [apiInstance, eventId, trackEvent]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const cashToken = searchParams.get("cashToken");

    if (cashToken) {
      setCashTokenNew(cashToken);
      trackEvent("Registration3_5_AppliedCoupon", {
        event_id: eventId,
        cash_token: cashToken,
        portal_type: "highschool",
      });
    }
  }, [location.search, eventId, trackEvent]);

  useEffect(() => {
    const fetchPricingOptions = async () => {
      try {
        const response = await GetEventCombinePrice(apiInstance, eventId);
        if (response.data) {
          setCombinePrice(response?.data?.combinePrice);
          trackEvent("Registration3_ViewedPricing", {
            event_id: eventId,
            combine_price: response?.data?.combinePrice,
            portal_type: "highschool",
          });
        }
      } catch (error) {}
    };

    fetchPricingOptions();
  }, []); // eslint-disable-line

  const validateCashToken = async (token) => {
    try {
      const response = await CheckValidCashToken(apiInstance, token);
      if (response.success === true) {
        return true;
      } else {
        toast.error("Invalid cash token.", { position: "top-center" });
        return false;
      }
    } catch (error) {
      toast.error("Failed to validate cash token.", { position: "top-center" });
    }
  };

  const checkIsEmailExist = async (email) => {
    try {
      const response = await CheckIsEmailExist(apiInstance, email);
      if (response?.data.data === false) {
        toast.warning("Email ID is already registered!", {
          position: "top-center",
        });
        return true;
      }
      return false;
    } catch (error) {
      toast.error("Failed to validate email address.", {
        position: "top-center",
      });
      return true;
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      confirmEmail: "",
      password: "",
      confirmPassword: "",
      age: "",
      gpa: "",
      highSchool: "",
      expectedYearOfGraduation: "",
      primaryPosition: "",
      secondaryPosition: "",
      sportsParticipation: "",
      city: "",
      state: "",
      universityOfInteres: "", // matches backend field name (trailing 't' absent in API contract)
      academicDegreeOfInteres: "", // matches backend field name (trailing 't' absent in API contract)
      role: "spotcombine",
      athletePhoneNumber: "",
      parentPhoneNumber: "",
      varsityYears: "",
      offersReceived: "no",
      athleteOffers: [],
      shouldUnregister: true,
      tshirtSize: "",
      offerAcceptanceStatus: "",
      offerAcceptanceSchool: "",
    },
  });

  const selectedState = watch("state");
  const selectedPosition = watch("primaryPosition");
  const selectedShirt = watch("tshirtSize");
  const offerAcceptanceStatus = watch("offerAcceptanceStatus");
  const isCommitted = offerAcceptanceStatus !== "";

  useEffect(() => {
    if (!isCommitted) {
      setValue("offerAcceptanceSchool", "");
      clearErrors("offerAcceptanceSchool");
    }
  }, [isCommitted, setValue, clearErrors]);

  const {
    fields: offerFields,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "athleteOffers",
  });
  const offersReceived = watch("offersReceived");

  const onSubmit = async (data) => {
    trackEvent("Registration2_FormSubmitted", {
      event_id: eventId,
      portal_type: "highschool",
    });

    const stripPosition = (position) => {
      if (!position) return "";
      return position.includes("(") ? position.split("(")[0].trim() : position;
    };

    const sanitizedData = {
      ...data,
      primaryPosition: stripPosition(
        data.primaryPosition === "None" ? "" : data.primaryPosition,
      ),
      secondaryPosition: stripPosition(
        data.secondaryPosition === "None" ? "" : data.secondaryPosition,
      ),
      sportsParticipation: Array.isArray(data.sportsParticipation)
        ? data.sportsParticipation.join(", ")
        : "",
    };

    const isEmailExist = await checkIsEmailExist(sanitizedData.email);
    if (isEmailExist) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      await PostCandidateAthlete(apiInstance, {
        firstName: sanitizedData.firstName,
        lastName: sanitizedData.lastName,
        email: sanitizedData.email,
        highSchool: sanitizedData.highSchool,
        expectedYearOfGraduation: sanitizedData.expectedYearOfGraduation,
        primaryPosition: sanitizedData.primaryPosition,
        athletePhoneNumber: sanitizedData.athletePhoneNumber,
        parentPhoneNumber: sanitizedData.parentPhoneNumber,
        city: sanitizedData.city,
        state: sanitizedData.state,
        eventId: eventId,
      });
      trackEvent("Registration2_CompletedForm", {
        event_id: eventId,
        portal_type: "highschool",
      });
    } catch (err) {
      console.error("Failed to save candidate athlete:", err);
    }

    try {
      if (cashTokenNew) {
        const isValid = await validateCashToken(cashTokenNew);
        if (!isValid) {
          toast.error("Invalid or expired cash token.", {
            position: "top-right",
          });
          return;
        }

        const registrationResponse = await registerAthleteWithEventId(
          apiInstance,
          sanitizedData,
          eventId,
        );

        if (registrationResponse.status === 200) {
          const userId = registrationResponse.data.id;
          const paymentPayload = {
            userId: userId,
            amount: combinePrice,
            isCombine: true,
            isPositionDrill: false,
            isAnnualSubscription: false,
            isMonthlySubscription: false,
            isCash: true,
            eventId: eventId,
            transactionId: "",
            intellipayToken: "",
          };

          await PostPayment(apiInstance, paymentPayload);
          trackEvent("Registration5_RegistrationCompleted", {
            event_id: eventId,
            payment_method: "cash",
            amount: combinePrice,
            portal_type: "highschool",
          });
          setLoading(false);
          setTimeout(() => navigate("/register/registrationsuccess"), 3000);
          setIsRegistered(true);
          reset();
        } else {
          trackEvent("Registration4_5_PaymentFailed", {
            event_id: eventId,
            reason: registrationResponse?.error ?? "unknown",
            portal_type: "highschool",
          });
          toast.error("Registration failed: " + registrationResponse.error, {
            position: "top-right",
          });
          return;
        }
      } else {
        const updatedData = { ...sanitizedData, eventId: eventId };
        dispatch(clearRegisterData());
        dispatch(registerAccountStore(updatedData));
        localStorage.clear();
        localStorage.setItem("registeredAccount", JSON.stringify(updatedData));
        trackEvent("Highschool_OpenedMembershipPlans", {
          event_id: eventId,
          portal_type: "highschool",
        });
        setLoading(false);
        navigate("/athlete-membership", { state: { fromSpotRegister: true } });
        reset();
        toast.success("Please complete the Payment.", {
          position: "top-right",
        });
      }
    } catch (error) {
      toast.error("Error Registering combine Athlete: " + error.error, {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (offersReceived === "yes" && offerFields.length === 0) {
      append({ schoolName: "", division: "", offerYear: "" });
    }
    if (offersReceived === "no" && offerFields.length > 0) {
      // wipe all rows in a single operation
      replace([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offersReceived]);

  return (
    <>
      <Nav />

      {!isRegistered && (
        <div className="w-full max-w-5xl p-6 mx-auto">
          {!cashTokenNew && <BackButton />}

          {/* Info area */}

          <div className="alert flex flex-row items-center bg-blue-100 p-3 rounded border-b-2 border-blue-300 mt-3">
            <div className="alert-icon flex items-center bg-blue-100 border-2 border-blue-500 justify-center h-8 w-8 flex-shrink-0 rounded-full">
              <span className="text-blue-500">
                <svg
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  className="h-6 w-6"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </span>
            </div>
            <div className="alert-content ml-4 w-full">
              <div className="alert-title font-semibold text-lg text-blue-700">
                Already registered for a previous combine?
              </div>
              <div className="alert-description text-sm text-blue-700">
                Please
                <a
                  className="text-blue-700 font-semibold hover:underline px-1"
                  href="/sign-in"
                >
                  Sign In
                </a>
                to your account, select this event from your dashboard and click
                Register.
              </div>
              <div className="alert-title font-semibold text-base text-blue-700 mt-3 pt-1 border-t border-blue-300 ">
                Event details will be sent after registration.
              </div>
            </div>
          </div>

          <div className="text-white text-left text-xl my-5">
            Selected Combine: {eventNameSelected}
          </div>
          <form
            className="container mx-auto "
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="mb-10">
              {/* Personal Information */}
              <div className="flex items-center gap-3 mt-2 mb-1">
                <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                  Personal Information
                </span>
                <div className="flex-1 h-px bg-gray-500" />
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6">
                <div className="form-field mt-4">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    {...register("firstName", { required: "Name is required" })}
                    placeholder="First Name"
                  />
                  {errors.firstName && (
                    <span className="text-red-500">
                      {errors.firstName?.message}
                    </span>
                  )}
                </div>
                <div className="form-field mt-4">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    {...register("lastName", {
                      required: "Last name is required",
                    })}
                    placeholder="Last Name"
                  />
                  {errors.lastName && (
                    <span className="text-red-500">
                      {errors.lastName?.message}
                    </span>
                  )}
                </div>

                <div className="form-field mt-4">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/,
                        message: "Invalid email address",
                      },
                    })}
                    placeholder="Email"
                  />
                  {errors.email && (
                    <span className="text-red-500">
                      {errors.email?.message}
                    </span>
                  )}
                </div>
                <div className="form-field mt-4">
                  <label htmlFor="confirmEmail">Confirm Email</label>
                  <input
                    id="confirmEmail"
                    type="email"
                    {...register("confirmEmail", {
                      required: "Please confirm your email",
                      validate: (v) =>
                        v === watch("email") || "Email addresses do not match",
                    })}
                    placeholder="Confirm Email"
                  />
                  {errors.confirmEmail && (
                    <span className="text-red-500">
                      {errors.confirmEmail?.message}
                    </span>
                  )}
                </div>
                <div className="form-field mt-4">
                  <label htmlFor="Password">Password</label>
                  <input
                    type="password"
                    aria-invalid={errors.password ? "true" : "false"}
                    name="password"
                    {...register("password", {
                      required: "Password is required",
                    })}
                    placeholder="Password"
                  />
                  {errors.password && (
                    <span className="text-red-500">
                      {errors.password?.message}
                    </span>
                  )}
                </div>
                <div className="form-field mt-4">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    {...register("confirmPassword", {
                      required: "Please confirm your password",
                      validate: (v) =>
                        v === watch("password") || "Passwords do not match",
                    })}
                    placeholder="Confirm Password"
                  />
                  {errors.confirmPassword && (
                    <span className="text-red-500">
                      {errors.confirmPassword?.message}
                    </span>
                  )}
                </div>
                <div className="form-field mt-4">
                  <label htmlFor="age">Age</label>
                  <input
                    type="number"
                    name="age"
                    {...register("age", { required: "Age is required" })}
                    placeholder="Age"
                    min={11}
                    max={30}
                    step={1}
                  />
                  {errors.age && (
                    <span className="text-red-500">{errors.age?.message}</span>
                  )}
                </div>
              </div>

              {/* Academic Details */}
              <div className="flex items-center gap-3 mt-8">
                <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                  Academic Details
                </span>
                <div className="flex-1 h-px bg-gray-500" />
              </div>

              <div className="grid sm:grid-cols-2 gap-x-6">
                <div className="form-field mt-4">
                  <label htmlFor="highSchool">High School</label>
                  <Controller
                    control={control}
                    name="highSchool"
                    rules={{ required: "High School is required" }}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <HighSchoolAutocomplete
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        apiInstance={apiInstance}
                        hasError={!!errors.highSchool}
                      />
                    )}
                  />
                  {errors.highSchool && (
                    <span className="text-red-500">
                      {errors.highSchool?.message}
                    </span>
                  )}
                </div>

                <div className="form-field mt-4">
                  <label htmlFor="gpa">GPA</label>
                  <input
                    type="text"
                    name="gpa"
                    {...register("gpa", { required: "GPA is required" })}
                    placeholder="e.g. 0.0"
                  />
                  {errors.gpa && (
                    <span className="text-red-500">{errors.gpa?.message}</span>
                  )}
                </div>
                <div className="form-field mt-4">
                  <label htmlFor="expectedYearOfGraduation">
                    Expected Year of Graduation
                  </label>
                  <input
                    type="number"
                    name="expectedYearOfGraduation"
                    {...register("expectedYearOfGraduation", {
                      required: "Expected Year of Graduation is required",
                    })}
                    placeholder="Expected Year of Graduation"
                    min={2015}
                    max={2035}
                    step={1}
                  />
                  {errors.expectedYearOfGraduation && (
                    <span className="text-red-500">
                      {errors.expectedYearOfGraduation?.message}
                    </span>
                  )}
                </div>
                <div className="form-field mt-4">
                  <label htmlFor="AcademicInterest">
                    Academic Degree of Interest
                  </label>
                  <input
                    type="text"
                    name="academicDegreeOfInteres"
                    {...register("academicDegreeOfInteres", {
                      required: "Academic Degree of Interest is required",
                    })}
                    placeholder="Academic Degree of Interest"
                  />
                  {errors.academicDegreeOfInteres && (
                    <span className="text-red-500">
                      {errors.academicDegreeOfInteres?.message}
                    </span>
                  )}
                </div>
              </div>

              {/* Athletic Information */}
              <div className="flex items-center gap-3 mt-8">
                <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                  Athletic Information
                </span>
                <div className="flex-1 h-px bg-gray-500" />
              </div>

              <div className="grid sm:grid-cols-2 gap-x-6">
                <div className="form-field mt-4">
                  <label
                    htmlFor="primaryPosition"
                    className="block text-base text-gray-900 dark:text-white"
                  >
                    Primary Position
                  </label>
                  <select
                    id="primaryPosition"
                    {...register("primaryPosition", {
                      required: "Primary Position is required",
                    })}
                    className={`bg-gray-50 border border-gray-300 text-md rounded-md form-select appearance-none bg-no-repeat ${
                      selectedPosition ? "text-gray-900" : "text-gray-400"
                    }`}
                  >
                    <option value="" disabled className="text-gray-400">
                      Select Primary Position
                    </option>
                    {primaryPositions.map((position) => (
                      <option
                        key={position}
                        value={position}
                        className="text-gray-900"
                      >
                        {position === "Interior Offensive Line"
                          ? "Interior Offensive Line (Guard or Center)"
                          : position}
                      </option>
                    ))}
                  </select>
                  {errors.primaryPosition && (
                    <span className="text-red-500">
                      {errors.primaryPosition.message}
                    </span>
                  )}
                </div>

                <div className="form-field mt-4">
                  <label
                    htmlFor="secondaryPosition"
                    className="block text-base text-gray-900 dark:text-white"
                  >
                    Secondary Position
                  </label>
                  <select
                    id="secondaryPosition"
                    className={`bg-gray-50 border border-gray-300 text-md rounded-md form-select appearance-none bg-no-repeat ${
                      watch("secondaryPosition")
                        ? "text-gray-900"
                        : "text-gray-400"
                    }`}
                    {...register("secondaryPosition")}
                  >
                    <option value="" disabled className="text-gray-400">
                      None
                    </option>
                    {positions.map((position) => (
                      <option
                        key={position}
                        value={position}
                        className="text-gray-900"
                      >
                        {position === "Interior Offensive Line"
                          ? "Interior Offensive Line (Guard or Center)"
                          : position}
                      </option>
                    ))}
                  </select>
                  {errors.secondaryPosition && (
                    <span className="text-red-500">
                      {errors.secondaryPosition.message}
                    </span>
                  )}
                </div>

                <div className="form-field mt-4">
                  <label
                    htmlFor="sportsParticipation"
                    className="block text-base text-gray-900"
                  >
                    Sports Participation
                  </label>
                  <Controller
                    control={control}
                    name="sportsParticipation"
                    rules={{ required: "At least one sport is required" }}
                    defaultValue={[]}
                    render={({ field: { onChange, value } }) => {
                      const sportsOptions = sports.map((sport, index) => ({
                        id: index,
                        name: sport,
                      }));

                      return (
                        <Multiselect
                          options={sportsOptions}
                          displayValue="name"
                          wrapperClass="rounded-md bg-gray-50 text-gray-900"
                          selectedValues={sportsOptions.filter((s) =>
                            value?.includes(s.name),
                          )}
                          onSelect={(selectedList) => {
                            const selected = selectedList.map(
                              (item) => item.name,
                            );
                            onChange(selected);
                          }}
                          onRemove={(selectedList) => {
                            const selected = selectedList.map(
                              (item) => item.name,
                            );
                            onChange(selected);
                          }}
                          placeholder="Select Sports"
                          className="bg-gray-50 text-md rounded-md form-select appearance-none bg-no-repeat text-gray-400"
                        />
                      );
                    }}
                  />
                  {errors.sportsParticipation && (
                    <span className="text-red-500">
                      {errors.sportsParticipation.message}
                    </span>
                  )}
                </div>

                <div className="form-field  mt-4">
                  <label htmlFor="athletePhone">
                    Years of Varsity Experience
                  </label>
                  <input
                    type="tel"
                    id="varsityYears"
                    inputMode="numeric"
                    placeholder="Years of Varsity Experience"
                    maxLength={15}
                    {...register("varsityYears", {
                      required: "Years of Varsity Experience is required",
                    })}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                  />
                  {errors.varsityYears && (
                    <span className="text-red-500 text-sm">
                      {errors.varsityYears?.message}
                    </span>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              <div className="flex items-center gap-3 my-6 ">
                <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                  Contact Information
                </span>
                <div className="flex-1 h-px bg-gray-500" />
              </div>

              <div className="grid sm:grid-cols-2 gap-x-6">
                <div className="form-field">
                  <label htmlFor="athletePhoneNumber">
                    Athlete Phone Number
                  </label>
                  <input
                    type="tel"
                    id="athletePhoneNumber"
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    placeholder="Athlete Phone Number"
                    maxLength={10}
                    {...register("athletePhoneNumber", {
                      required: "Athlete Phone Number is required",
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: "Enter a valid 10-digit phone number",
                      },
                    })}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                  />
                  {errors.athletePhoneNumber && (
                    <span className="text-red-500 text-sm">
                      {errors.athletePhoneNumber?.message}
                    </span>
                  )}
                </div>

                <div className="form-field">
                  <label htmlFor="parentPhoneNumber">Parent Phone Number</label>
                  <input
                    type="tel"
                    id="parentPhoneNumber"
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    placeholder="Parent Phone Number"
                    maxLength={10}
                    {...register("parentPhoneNumber", {
                      required: "Parent Phone Number is required",
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: "Enter a valid 10-digit phone number",
                      },
                    })}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                  />
                  {errors.parentPhoneNumber && (
                    <span className="text-red-500 text-sm">
                      {errors.parentPhoneNumber?.message}
                    </span>
                  )}
                </div>

                <div className="form-field mt-4">
                  <label htmlFor="city">City</label>
                  <input
                    type="text"
                    name="city"
                    {...register("city", { required: "City is required" })}
                    placeholder="City"
                  />
                  {errors.city && (
                    <span className="text-red-500">{errors.city?.message}</span>
                  )}
                </div>
                <div className="form-field mt-4">
                  <label htmlFor="state">State</label>
                  <select
                    name="state"
                    className={`bg-gray-50 border border-gray-300 text-md rounded-md form-select appearance-none bg-no-repeat ${
                      selectedState ? "text-gray-900" : "text-gray-400"
                    }`}
                    {...register("state", { required: "State is required" })}
                    style={{ backgroundColor: "white" }}
                  >
                    <option value="" className="text-gray-400" disabled>
                      Select a State
                    </option>
                    {states.map((state) => (
                      <option
                        className="text-gray-900"
                        key={state}
                        value={state}
                      >
                        {state}
                      </option>
                    ))}
                  </select>
                  {errors.state && (
                    <span className="text-red-500">
                      {errors.state?.message}
                    </span>
                  )}
                </div>
              </div>

              {/* Preferences */}
              <div className="flex items-center gap-3 my-6">
                <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                  Preferences
                </span>
                <div className="flex-1 h-px bg-gray-500" />
              </div>

              <div className="grid sm:grid-cols-2 gap-x-6">
                <div className="form-field">
                  <label htmlFor="tshirtSize">T-Shirt Size</label>
                  <select
                    id="tshirtSize"
                    {...register("tshirtSize", {
                      required: "T-Shirt Size is required",
                    })}
                    className={`bg-gray-50 border border-gray-300 text-md rounded-md form-select appearance-none bg-no-repeat ${
                      selectedShirt ? "text-gray-900" : "text-gray-400"
                    }`}
                  >
                    <option value="" disabled className="text-gray-400">
                      Select T-Shirt Size
                    </option>

                    {tshirtSizes.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  {errors.tshirtSize && (
                    <span className="text-red-500">
                      {errors.tshirtSize.message}
                    </span>
                  )}
                </div>

                <div className="form-field ">
                  <label htmlFor="universityOfInteres">
                    Universities You are Most Interested in Playing For
                  </label>
                  <input
                    type="text"
                    name="universityOfInteres"
                    {...register("universityOfInteres", {
                      required: true,
                    })}
                    placeholder="Universities You are Most Interested in Playing For"
                  />
                </div>
              </div>

              {/* Offers */}
              <div className="flex items-center gap-3 my-6">
                <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                  Offers
                </span>
                <div className="flex-1 h-px bg-gray-500" />
              </div>

              <div className="form-field mt-6">
                <label className="block mb-2 font-semibold">
                  Have you received any offers?
                </label>

                <div className="flex items-center gap-6">
                  {/* YES radio */}
                  {(() => {
                    const offersReceivedRegister = register("offersReceived");
                    return (
                      <>
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            value="yes"
                            className="mr-2"
                            {...offersReceivedRegister}
                            onChange={(e) => {
                              offersReceivedRegister.onChange(e);
                              if (
                                e.target.value === "yes" &&
                                offerFields.length === 0
                              ) {
                                append({
                                  schoolName: "",
                                  division: "",
                                  offerYear: "",
                                });
                              }
                            }}
                          />
                          <span>Yes</span>
                        </label>

                        {/* NO radio */}
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            value="no"
                            className="mr-2"
                            {...offersReceivedRegister}
                            onChange={(e) => {
                              offersReceivedRegister.onChange(e);
                              if (e.target.value === "no") {
                                remove(offerFields.map((_, idx) => idx));
                              }
                            }}
                          />
                          <span>No</span>
                        </label>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Offer rows (only if they answered Yes) */}
              {offersReceived === "yes" && (
                <div className="mt-4 space-y-3">
                  {offerFields.map((field, index) => (
                    <div
                      key={field.id}
                      className="grid sm:grid-cols-4 gap-3 items-end border border-gray-200 rounded-md p-3"
                    >
                      {/* School */}
                      <div className="form-field">
                        <label className="block mb-1">School</label>
                        <input
                          placeholder="School"
                          {...register(`athleteOffers.${index}.schoolName`, {
                            required: "School is required",
                          })}
                        />
                        {errors.athleteOffers?.[index]?.schoolName && (
                          <span className="text-red-500 text-sm">
                            {errors.athleteOffers[index].schoolName?.message}
                          </span>
                        )}
                      </div>

                      {/* Division */}
                      <div className="form-field">
                        <label className="block mb-1">Division</label>
                        <select
                          defaultValue=""
                          {...register(`athleteOffers.${index}.division`, {
                            required: "Division is required",
                          })}
                          className="bg-gray-50 border border-gray-300 rounded-md w-full"
                        >
                          <option value="">Select</option>

                          {divisions.map((divisionItem) => (
                            <option key={divisionItem} value={divisionItem}>
                              {divisionItem}
                            </option>
                          ))}
                        </select>
                        {errors.athleteOffers?.[index]?.division && (
                          <span className="text-red-500 text-sm">
                            {errors.athleteOffers[index].division?.message}
                          </span>
                        )}
                      </div>

                      {/* Year */}
                      <div className="form-field">
                        <label className="block mb-1">Year</label>
                        <input
                          type="number"
                          placeholder="offer Year"
                          {...register(`athleteOffers.${index}.offerYear`, {
                            required: "Year is required",
                            validate: (v) =>
                              (/^\d{4}$/.test(String(v)) &&
                                Number(v) >= 1900) ||
                              "Enter a valid 4-digit year",
                          })}
                        />
                        {errors.athleteOffers?.[index]?.offerYear && (
                          <span className="text-red-500 text-sm">
                            {errors.athleteOffers[index].offerYear?.message}
                          </span>
                        )}
                      </div>

                      {/* Remove row (X) */}
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="text-red-600 font-bold px-2 py-1 border border-red-400 rounded hover:bg-red-50"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() =>
                      append({ schoolName: "", division: "", offerYear: "" })
                    }
                    className="mt-2 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white hover:bg-gray-50"
                  >
                    Add another
                  </button>

                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <p className="block mb-2 font-semibold">
                      Have you accepted any of these offers?
                    </p>
                    <div className="grid sm:grid-cols-2 gap-x-6">
                      <div className="form-field">
                        <label htmlFor="offerAcceptanceStatus">
                          Offer Status
                        </label>
                        <select
                          id="offerAcceptanceStatus"
                          {...register("offerAcceptanceStatus")}
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-md rounded-md form-select appearance-none bg-no-repeat"
                        >
                          <option value="">No</option>
                          <option value="Committed">
                            I have made a verbal commitment
                          </option>
                          <option value="Signed">
                            I have signed a letter of intent
                          </option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label htmlFor="offerAcceptanceSchool">School</label>
                        <input
                          type="text"
                          id="offerAcceptanceSchool"
                          placeholder="School name"
                          disabled={!isCommitted}
                          {...register("offerAcceptanceSchool", {
                            validate: (v) => {
                              const status = getValues("offerAcceptanceStatus");
                              return (
                                status === "" ||
                                v?.trim().length > 0 ||
                                "School is required"
                              );
                            },
                          })}
                          className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            !isCommitted
                              ? "opacity-50 cursor-not-allowed bg-gray-100"
                              : ""
                          }`}
                        />
                        {errors.offerAcceptanceSchool && (
                          <span className="text-red-500 text-sm">
                            {errors.offerAcceptanceSchool.message}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Waiver & Liability Agreement */}
              <div className="flex items-center gap-3 my-6">
                <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                  Waiver & Liability Agreement
                </span>
                <div className="flex-1 h-px bg-gray-500" />
              </div>

              {/* Waiver & Liability Agreement */}
              <div className="mt-6 p-3 border rounded-md bg-gray-50">
                {/* Scrollable Text Box */}
                <div className="h-40 overflow-y-scroll border p-3 bg-white rounded-md text-sm leading-6">
                  <p className="text-sm">
                    I hereby acknowledge that I am, or my child is, voluntarily
                    participating in a Combine organized by Track It Verified,
                    LLC ("the Activity"). In consideration for being permitted
                    to participate in this activity, I hereby agree as follows:
                  </p>
                  <ol className="list-decimal list-inside text-sm mt-2">
                    <li className="pb-3">
                      {" "}
                      I understand and acknowledge that there are certain risks
                      and dangers associated with the Activity, including but
                      not limited to: getting injured while performing the
                      combine drills.{" "}
                    </li>
                    <li className="pb-3">
                      On behalf of myself (if 18 or older) or on behalf of my
                      child, I/we voluntarily assume all risks related to
                      participation in the Activity and agree to release and
                      hold harmless Track It Verified, LLC, its officers,
                      employees, volunteers, and agents from any and all claims,
                      damages, liabilities, costs, and expenses that may arise
                      as a result of participation in the Activity, including
                      but not limited to injury or loss.{" "}
                    </li>
                    <li className="pb-3">
                      On behalf of myself (if 18 or older) or on behalf of my
                      child, I/we agree to indemnify and defend Track It
                      Verified, LLC, its officers, employees, volunteers, and
                      agents against any and all claims, damages, liabilities,
                      costs, and expenses, including attorney fees, arising from
                      or in connection with participation in the Activity.{" "}
                    </li>
                    <li className="pb-3">
                      On behalf of myself (if 18 or older) or on behalf of my
                      child, I/we certify that I or my child is in good health
                      and have/has no physical or mental conditions that prevent
                      me or my child from safely participating in the Activity.{" "}
                    </li>
                    <li className="pb-3">
                      On behalf of myself (if 18 or older) or on behalf of my
                      child, I grant Track It Verified, LLC the right to use
                      photographs, video recordings, or other media of me or my
                      child taken during the Activity for promotional or
                      marketing purposes. If I am signing this on behalf of my
                      child, I understand that Pursuant to the Children’s Online
                      Policy Protection Act (“COPPA”), Track It Verified, LLC,
                      located at 1899 Oak Lane, Provo, Utah 84604, wishes to
                      collect personal information from my child. The “personal
                      information” of the Child includes, images, photographs,
                      video recordings, name acknowledgment, video tapings,
                      statistics, height, weight and other information
                      pertaining to the Child while participating in the
                      Activity. I understand that Parent/Guardian consent is
                      required for the collection, use and disclosure of the
                      child's personal information. I hereby acknowledge that I
                      am the Parent/Guardian of the child listed above and
                      consent to Track It Verified, LLC's collection, use, and
                      disclosure of the child's personal information by signing
                      this document. I understand that I have the option to
                      consent to the collection and use of the child's personal
                      information without consenting to disclosure of the
                      child's information to third parties. I understand that my
                      failure to consent to Track It Verified, LLC's collection
                      use and disclosure of the child's personal information
                      will not prevent the child from participating in the
                      Activity. Track It Verified, LLC, will not require a child
                      to disclose more information than is reasonably necessary
                      to participate in the Activity as a condition of
                      participation. Parent/Guardian can review the child's
                      personal information, ask to have it deleted, and refuse
                      to allow any further collection use or disclosure of the
                      child's information. Track It Verified, LLC has
                      established and maintains reasonable procedures to protect
                      the security and integrity of the personal information
                      collected from the child. If the Parent/Guardian does not
                      wish to provide consent for Track It Verified, LLC's
                      collection, use, and disclosure of the child's personal
                      information or seeks to revoke previously granted consent,
                      Parent/Guardian must contact Track It Verified, LLC at:
                      trackittverified@gmail.com and make this request directly.
                      Parent/Guardian releases Track It Verified, LLC, its
                      employees, contractors, and each and all persons and
                      parties involved from any liability connected with the
                      taking, recording, or publication of said photographs,
                      video, or computer images involving the Child.
                      Parent/Guardian waives all rights he/she may have to any
                      claims for any payment or royalties in connection with any
                      exhibition, televising, or other publication of these
                      materials, regardless of the purpose or sponsoring of such
                      exhibiting, broadcasting, or other publication. All
                      negatives and positives, whether prints, digital video,
                      film or video, are the exclusive property of Track It
                      Verified, LLC or the person or entity designated by it,
                      solely and completely.
                    </li>
                  </ol>
                  <p className="mt-1 text-sm">
                    I HAVE READ THIS RELEASE, IF SIGNING MYSELF OR ON BEHALF OF
                    MY CHILD, AND FULLY UNDERSTAND ITS TERMS, AND UNDERSTAND
                    THAT I AM SIGNING FREELY AND VOLUNTARILY WITHOUT ANY
                    INDUCEMENT.
                  </p>
                </div>
                {/* Checkbox Requirement */}
                <div className="flex items-center gap-2 mt-4">
                  <input
                    type="checkbox"
                    {...register("agreedToWaiver", { required: true })}
                    className="w-4 h-4"
                  />
                  <label className="text-sm">
                    I have read and agree to the Waiver & Liability Agreement.
                  </label>
                </div>

                {errors.agreedToWaiver && (
                  <p className="text-red-500 text-xs mt-1">
                    You must accept the waiver before registering.
                  </p>
                )}
              </div>
              {/* Waiver & Liability Agreement end*/}
              <div className="form-field mt-4">
                {loading && <LoadingSpinner />}
              </div>
              <button
                type="submit"
                className="bg-red-500 text-white uppercase rounded px-8 py-2 text-center mt-4 hover:bg-red-700 cursor-pointer w-full"
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      )}
      <ToastContainer />
    </>
  );
};

export default RegisterAthlete;
